import {
  Box,
  Form,
  FormErrorTypes,
  Modal,
  ModalContext,
  Text,
  useFormState,
  useFormValidation,
  useServerResponseTranslation,
} from "dash-ui";
import {
  useGetAllFoldersInPropertyQuery,
  useMoveMediaToFolderMutation,
} from "gql/generated.graphql";
import { FormEvent, useContext, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { DEFAULT_ROOT_MEDIA_FOLDER_NAME } from "dash-common";
import { FolderBrowser } from "../FolderBrowser";

enum FormFields {
  MediaFolderId = "mediaFolderId",
}

// Styled Modal with custom width
const StyledMoveFileModal = styled(Modal)`
  &.move-file-modal__content {
    width: 90% !important;
    max-width: 600px !important;
    margin: 50px auto !important;
    left: 0 !important;
    right: 0 !important;
    position: relative !important;

    @media ${({ theme }) => theme.devices.desktop} {
      width: 600px !important;
    }
  }
`;

const MoveFileToFolderModal = ({
  onCompleted,
}: {
  onCompleted?: () => void;
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["forms", "common", "pages"]);
  const { isOpenCurrent, toggleModal, setPayload, payload } =
    useContext(ModalContext);
  const [
    formState,
    [setMediaFolderId],
    [formOnSubmit, formOnError, , , resetForm],
  ] = useFormState([FormFields.MediaFolderId]);

  const isOpen = isOpenCurrent["MoveFileToFolderModal"];

  // State for folder navigation
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [selectedFolderId, setSelectedFolderId] = useState<string | null>(null);

  const { data, loading: allMediaLoading } = useGetAllFoldersInPropertyQuery({
    variables: {
      propertyId: payload?.propertyId,
      includeRootFolder: true, // Include root folder to allow moving files back to it
      excludeSlideshowFolder: false, // Include slideshow folder for moves
    },
    fetchPolicy: "network-only",
    skip: !payload?.propertyId || !isOpen,
  });
  const [formErrors, , [setMediaFolderIdError], [resetFormErrors]] =
    useFormValidation([FormFields.MediaFolderId]);

  const translateErrorMessage = useServerResponseTranslation();
  const [moveMediaToFolderMutation, { loading: moveLoading }] =
    useMoveMediaToFolderMutation();

  const formRef = useRef<HTMLFormElement | null>(null);
  const folders = useMemo(() => data?.getAllFoldersInProperty || [], [data]);

  // Find root folder ID
  const rootFolderId = useMemo(() => {
    const rootFolder = folders.find(
      (f) => f.folderName === DEFAULT_ROOT_MEDIA_FOLDER_NAME
    );
    return rootFolder?._id || null;
  }, [folders]);

  const onModalClose = () => {
    toggleModal(false, "MoveFileToFolderModal");
    setPayload(null);
    resetFormErrors();
    resetForm();
    setCurrentFolderId(null);
    setSelectedFolderId(null);
  };

  const onFolderSelect = (folderId: string) => {
    // Handle root folder selection
    const actualFolderId = folderId === "" ? rootFolderId : folderId;
    setSelectedFolderId(actualFolderId);
    setMediaFolderId(actualFolderId || "");
    setMediaFolderIdError(null);
  };

  const onFolderNavigate = (folderId: string | null) => {
    setCurrentFolderId(folderId);
  };

  const onSubmitForm = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    e.stopPropagation();
    formOnSubmit();

    const { mediaFolderId } = formState;

    if (!mediaFolderId) {
      return setMediaFolderIdError({
        type: FormErrorTypes.Invalid,
        message: t("forms:messages.selectFolder"),
      });
    }

    moveMediaToFolderMutation({
      variables: {
        mediaFolderId,
        mediaIds: payload?.mediaIds,
        filter: payload?.filter,
        excludedIds: payload?.excludedIds ?? undefined,
      },
      ...(onCompleted && { onCompleted }),
    })
      .then(onModalClose)
      .catch((error) => {
        formOnError(translateErrorMessage(error));
      });
    return null;
  };

  return (
    <StyledMoveFileModal
      isOpen={isOpen}
      onRequestClose={onModalClose}
      title={t("pages:propertyPage.moveFileModalTitle") || ""}
      className="move-file-modal"
      submitButton={{
        title: t("common:labels.move"),
        isLoading: moveLoading,
        disabled: moveLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{
        title: t("common:labels.cancel"),
        onClick: onModalClose,
      }}
    >
      <Box style={{ width: "100%", minWidth: "300px" }}>
        <Form ref={formRef} onSubmit={onSubmitForm}>
          <Box my={3}>
            <Text color={theme.colors.n80}>
              {t("pages:propertyPage.moveFileModalText")}
            </Text>
          </Box>

          <Box>
            <Box mb={4}>
              <Text>
                <strong>Selected folder:</strong>{" "}
                {selectedFolderId ? (
                  <span style={{ color: theme.colors.b50 }}>
                    {selectedFolderId === rootFolderId
                      ? "Root"
                      : folders.find((f) => f._id === selectedFolderId)
                          ?.folderName || "Unknown"}
                  </span>
                ) : (
                  <span style={{ color: theme.colors.n60 }}>
                    No folder selected
                  </span>
                )}
              </Text>
            </Box>

            <FolderBrowser
              folders={folders as any}
              loading={allMediaLoading}
              onFolderSelect={onFolderSelect}
              onFolderNavigate={onFolderNavigate}
              selectedFolderId={selectedFolderId}
              currentFolderId={currentFolderId}
            />

            {formErrors[FormFields.MediaFolderId] && (
              <Box mt={2}>
                <Text color="error">
                  {formErrors[FormFields.MediaFolderId]?.message}
                </Text>
              </Box>
            )}
          </Box>
        </Form>
      </Box>
    </StyledMoveFileModal>
  );
};

export default MoveFileToFolderModal;
